import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FileText,
  Save,
  X,
  Plus,
  Trash2,
  Search,
  Calendar,
  AlertCircle,
  ArrowLeft,
  UserPlus,
  User,
  Phone,
  Mail,
  MapPin
} from 'lucide-react'
import { Invoice, InvoiceFormData, Product, Customer } from '../types/api'
import { apiClient } from '../services/api'

interface LineItem {
  id?: string
  product_id: string
  product_name: string
  quantity: number
  unit_price: number
  total_price: number
}

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditMode = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Customer autocomplete
  const [customerSearch, setCustomerSearch] = useState('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [searchingCustomers, setSearchingCustomers] = useState(false)
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)

  // Product autocomplete
  const [productSearch, setProductSearch] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [searchingProducts, setSearchingProducts] = useState(false)
  const [showCustomProductModal, setShowCustomProductModal] = useState(false)

  // Inline customer creation
  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    gst_number: '',
    address: '',
    city: '',
    state: '',
    postal_code: ''
  })

  const [formData, setFormData] = useState<{
    customer_id: string
    invoice_date: string
    due_date: string
    payment_terms: string
    payment_method: string
    notes: string
    place_of_supply: string
  }>({
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    payment_terms: 'Net 30',
    payment_method: 'invoice',
    notes: '',
    place_of_supply: ''
  })

  const [lineItems, setLineItems] = useState<LineItem[]>([])

  // Load invoice data for editing
  useEffect(() => {
    if (id && isEditMode) {
      loadInvoiceData()
    }
  }, [id, isEditMode])

  // Customer search with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (customerSearch.length >= 2) {
        await searchCustomers(customerSearch)
      } else if (customerSearch.length === 0) {
        setCustomers([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [customerSearch])

  // Product search with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (productSearch.length >= 2) {
        await searchProducts(productSearch)
      } else if (productSearch.length === 0) {
        setProducts([])
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [productSearch])

  const searchCustomers = async (query: string) => {
    try {
      setSearchingCustomers(true)
      const response = await apiClient.getCustomers()
      // Handle nested data structure from API
      const customersList = response.data?.customers || response.customers || []
      const filtered = customersList.filter((c: Customer) =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.phone?.includes(query) ||
        c.email?.toLowerCase().includes(query.toLowerCase())
      )
      setCustomers(filtered)
      setShowCustomerDropdown(true)
    } catch (err: any) {
      console.error('Error searching customers:', err)
    } finally {
      setSearchingCustomers(false)
    }
  }

  const searchProducts = async (query: string) => {
    try {
      setSearchingProducts(true)
      console.log('Searching products with query:', query)
      const response = await apiClient.getProducts({
        search: query,
        limit: 100  // Increased limit and removed is_active filter
      })
      console.log('Products response:', response)
      // API client already unwraps the nested structure
      setProducts(response.products || [])
    } catch (err: any) {
      console.error('Error searching products:', err)
    } finally {
      setSearchingProducts(false)
    }
  }

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      setError('Customer name and phone are required')
      return
    }

    try {
      const response = await apiClient.createCustomer(newCustomer)
      // Handle nested data structure from API
      const createdCustomer = response.data?.customer || response.customer || response.data

      // Select the new customer
      setSelectedCustomer(createdCustomer)
      setCustomerSearch('') // Clear search input
      setFormData({ ...formData, customer_id: createdCustomer.id })
      setCustomers([]) // Clear dropdown results

      // Reset form
      setNewCustomer({
        name: '',
        phone: '',
        email: '',
        gst_number: '',
        address: '',
        city: '',
        state: '',
        postal_code: ''
      })
      setShowAddCustomer(false)
      setError(null)
    } catch (err: any) {
      setError('Failed to add customer: ' + err.message)
    }
  }

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer)
    setCustomerSearch('') // Clear search input
    setFormData({ ...formData, customer_id: customer.id })
    setShowCustomerDropdown(false)
    setCustomers([]) // Clear dropdown results
  }

  const loadInvoiceData = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getInvoice(id!)
      const invoice = response.data || response

      // Set form data
      setFormData({
        customer_id: invoice.customer_id || '',
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        payment_terms: invoice.payment_terms || 'Net 30',
        payment_method: invoice.payment_method,
        notes: invoice.notes || '',
        place_of_supply: invoice.place_of_supply || ''
      })

      // Load and set customer for autocomplete
      if (invoice.customer_id) {
        try {
          const customersRes = await apiClient.getCustomers()
          // Handle nested data structure from API
          const customersList = customersRes.data?.customers || customersRes.customers || []
          const customer = customersList.find((c: Customer) => c.id === invoice.customer_id)
          if (customer) {
            setSelectedCustomer(customer)
            setCustomerSearch('') // Clear search, show full details instead
          }
        } catch (err) {
          console.error('Error loading customer:', err)
        }
      }

      // Set line items
      if (invoice.items) {
        setLineItems(
          invoice.items.map((item: any) => ({
            id: item.id,
            product_id: item.product_id,
            product_name: item.product_name,
            quantity: parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price),
            total_price: parseFloat(item.total_price)
          }))
        )
      }
    } catch (err: any) {
      console.error('Error loading invoice:', err)
      setError('Failed to load invoice data')
    } finally {
      setLoading(false)
    }
  }

  const addLineItem = (product: Product) => {
    const existingItem = lineItems.find((item) => item.product_id === product.id)

    if (existingItem) {
      // Increase quantity if product already exists
      setLineItems(
        lineItems.map((item) =>
          item.product_id === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                total_price: (item.quantity + 1) * item.unit_price
              }
            : item
        )
      )
    } else {
      // Add new line item
      const price = parseFloat(product.price.toString())
      setLineItems([
        ...lineItems,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          unit_price: price,
          total_price: price
        }
      ])
    }

    // Clear search to allow adding more products
    setProductSearch('')
  }

  const updateLineItem = (index: number, field: keyof LineItem, value: any) => {
    const updatedItems = [...lineItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }

    // Recalculate total if quantity or unit_price changed
    if (field === 'quantity' || field === 'unit_price') {
      updatedItems[index].total_price =
        updatedItems[index].quantity * updatedItems[index].unit_price
    }

    setLineItems(updatedItems)
  }

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index))
  }

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total_price, 0)
    const taxAmount = 0 // Tax will be calculated based on place of supply and GST rates
    const total = subtotal + taxAmount

    return { subtotal, taxAmount, total }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.customer_id) {
      setError('Please select a customer')
      return
    }

    if (lineItems.length === 0) {
      setError('Please add at least one item')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { taxAmount } = calculateTotals()

      const invoiceData: InvoiceFormData = {
        customer_id: formData.customer_id,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        payment_terms: formData.payment_terms,
        payment_method: formData.payment_method,
        notes: formData.notes,
        tax_amount: taxAmount,
        items: lineItems.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_price: item.unit_price
        }))
      }

      if (isEditMode) {
        await apiClient.updateInvoice(id!, invoiceData)
      } else {
        await apiClient.createInvoice(invoiceData)
      }

      navigate('/invoices')
    } catch (err: any) {
      console.error('Error saving invoice:', err)
      setError(err.message || 'Failed to save invoice')
    } finally {
      setLoading(false)
    }
  }

  const { subtotal, taxAmount, total } = calculateTotals()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount)
  }

  if (loading && isEditMode) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/invoices')}
            className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8" />
              {isEditMode ? 'Edit Invoice' : 'New Invoice'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
              {isEditMode ? 'Update invoice details' : 'Create a new credit sale invoice'}
            </p>
          </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer and Date Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Customer Selection with Autocomplete */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Customer *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAddCustomer(!showAddCustomer)}
                      className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                    >
                      <UserPlus className="w-4 h-4" />
                      {showAddCustomer ? 'Cancel' : 'Add New Customer'}
                    </button>
                  </div>

                  {!showAddCustomer ? (
                    <div className="relative">
                      {/* Selected customer full details */}
                      {selectedCustomer ? (
                        <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="w-5 h-5 text-orange-600" />
                              <h3 className="font-semibold text-orange-900">{selectedCustomer.name}</h3>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(null)
                                setCustomerSearch('')
                                setFormData({ ...formData, customer_id: '' })
                              }}
                              className="text-orange-600 hover:text-orange-800"
                              title="Change customer"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="space-y-1 text-sm">
                            {selectedCustomer.phone && (
                              <div className="flex items-center gap-2 text-orange-700">
                                <Phone className="w-4 h-4" />
                                {selectedCustomer.phone}
                              </div>
                            )}
                            {selectedCustomer.email && (
                              <div className="flex items-center gap-2 text-orange-700">
                                <Mail className="w-4 h-4" />
                                {selectedCustomer.email}
                              </div>
                            )}
                            {selectedCustomer.gst_number && (
                              <div className="flex items-center gap-2 text-orange-700">
                                <FileText className="w-4 h-4" />
                                GST: {selectedCustomer.gst_number}
                              </div>
                            )}
                            {selectedCustomer.address && (
                              <div className="flex items-start gap-2 text-orange-700">
                                <MapPin className="w-4 h-4 mt-0.5" />
                                <span>
                                  {selectedCustomer.address}
                                  {selectedCustomer.city && `, ${selectedCustomer.city}`}
                                  {selectedCustomer.state && `, ${selectedCustomer.state}`}
                                  {selectedCustomer.pincode && ` - ${selectedCustomer.pincode}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Search input */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search customer by name, phone, or email..."
                              value={customerSearch}
                              onChange={(e) => {
                                setCustomerSearch(e.target.value)
                                setShowCustomerDropdown(true)
                              }}
                              onFocus={() => {
                                if (customers.length > 0) setShowCustomerDropdown(true)
                              }}
                              required={!formData.customer_id}
                              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {/* Loading indicator */}
                            {searchingCustomers && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                              </div>
                            )}
                          </div>

                          {/* Customer Dropdown */}
                          {showCustomerDropdown && customers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                              {customers.map((customer) => (
                                <button
                                  key={customer.id}
                                  type="button"
                                  onClick={() => handleCustomerSelect(customer)}
                                  className="w-full p-3 text-left hover:bg-orange-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                >
                                  <div className="font-medium text-gray-900">{customer.name}</div>
                                  <div className="text-sm text-gray-500">
                                    {customer.phone && `${customer.phone}`}
                                    {customer.email && ` • ${customer.email}`}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Name *"
                          value={newCustomer.name}
                          onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Phone *"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={newCustomer.email}
                          onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="GST Number"
                          value={newCustomer.gst_number}
                          onChange={(e) => setNewCustomer({ ...newCustomer, gst_number: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="City"
                          value={newCustomer.city}
                          onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="State"
                          value={newCustomer.state}
                          onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Address"
                        value={newCustomer.address}
                        onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomer}
                        className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                      >
                        Add Customer
                      </button>
                    </div>
                  )}
                </div>

                {/* Invoice Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) =>
                      setFormData({ ...formData, invoice_date: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Due Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) =>
                      setFormData({ ...formData, due_date: e.target.value })
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Payment Terms */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) =>
                      setFormData({ ...formData, payment_terms: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Due on Receipt">Due on Receipt</option>
                    <option value="Net 7">Net 7</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 60">Net 60</option>
                    <option value="Net 90">Net 90</option>
                  </select>
                </div>

                {/* Place of Supply */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Place of Supply
                  </label>
                  <select
                    value={formData.place_of_supply}
                    onChange={(e) =>
                      setFormData({ ...formData, place_of_supply: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select State</option>
                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                    <option value="Assam">Assam</option>
                    <option value="Bihar">Bihar</option>
                    <option value="Chhattisgarh">Chhattisgarh</option>
                    <option value="Goa">Goa</option>
                    <option value="Gujarat">Gujarat</option>
                    <option value="Haryana">Haryana</option>
                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                    <option value="Jharkhand">Jharkhand</option>
                    <option value="Karnataka">Karnataka</option>
                    <option value="Kerala">Kerala</option>
                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                    <option value="Maharashtra">Maharashtra</option>
                    <option value="Manipur">Manipur</option>
                    <option value="Meghalaya">Meghalaya</option>
                    <option value="Mizoram">Mizoram</option>
                    <option value="Nagaland">Nagaland</option>
                    <option value="Odisha">Odisha</option>
                    <option value="Punjab">Punjab</option>
                    <option value="Rajasthan">Rajasthan</option>
                    <option value="Sikkim">Sikkim</option>
                    <option value="Tamil Nadu">Tamil Nadu</option>
                    <option value="Telangana">Telangana</option>
                    <option value="Tripura">Tripura</option>
                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                    <option value="Uttarakhand">Uttarakhand</option>
                    <option value="West Bengal">West Bengal</option>
                    <option value="Delhi">Delhi</option>
                    <option value="Puducherry">Puducherry</option>
                    <option value="Chandigarh">Chandigarh</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>

              {/* Product Search with Autocomplete - Always Visible */}
              <div className="mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products by name, SKU, or barcode..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                    {searchingProducts && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                      </div>
                    )}
                  </div>

                  {/* Add Custom Product Button */}
                  <button
                    type="button"
                    onClick={() => setShowCustomProductModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap font-medium"
                    title="Add custom product"
                  >
                    <Plus className="w-5 h-5" />
                    Add Custom
                  </button>
                </div>

                {/* Product Results Dropdown */}
                {productSearch.length >= 2 && (
                  <div className="relative">
                    <div className="absolute z-10 w-full mt-1 max-h-80 overflow-y-auto border-2 border-orange-200 rounded-lg bg-white shadow-xl">
                      {searchingProducts ? (
                        <div className="p-6 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
                          Searching products...
                        </div>
                      ) : products.length === 0 ? (
                        <div className="p-6 text-center">
                          <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p className="text-gray-500 font-medium">No products found</p>
                          <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                        </div>
                      ) : (
                        products.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => addLineItem(product)}
                            className="w-full p-4 text-left hover:bg-orange-50 border-b border-gray-100 last:border-b-0 transition-colors group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                                  {product.name}
                                </div>
                                <div className="text-sm text-gray-500 mt-1 flex items-center gap-3">
                                  {product.sku && (
                                    <span className="flex items-center gap-1">
                                      <span className="font-medium">SKU:</span> {product.sku}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <span className="font-medium">Stock:</span> {product.stock_quantity} {product.unit}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right ml-4">
                                <div className="text-lg font-bold text-orange-600">
                                  {formatCurrency(parseFloat(product.price.toString()))}
                                </div>
                                <button className="mt-1 px-3 py-1 bg-blue-100 text-orange-700 rounded text-sm font-medium group-hover:bg-orange-600 group-hover:text-white transition-colors">
                                  Add
                                </button>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {productSearch.length > 0 && productSearch.length < 2 && (
                  <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-sm text-orange-700 text-center">
                      Type at least 2 characters to search products...
                    </p>
                  </div>
                )}
              </div>

              {/* Line Items Table */}
              {lineItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No items added yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          Product
                        </th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                          Quantity
                        </th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                          Unit Price
                        </th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                          Total
                        </th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {lineItems.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-gray-900">
                              {item.product_name}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) =>
                                updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)
                              }
                              className="w-24 px-2 py-1 text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) =>
                                updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)
                              }
                              className="w-32 px-2 py-1 text-right border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {formatCurrency(item.total_price)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                placeholder="Add any additional notes or terms..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Summary Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Summary</h2>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                </div>
                {taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax (GST)</span>
                    <span className="font-medium text-gray-900">{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between">
                    <span className="text-base font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-orange-600">
                      {formatCurrency(total)}
                    </span>
                  </div>
                </div>
                {formData.place_of_supply && (
                  <div className="pt-2 text-xs text-gray-500">
                    Place of Supply: {formData.place_of_supply}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                <button
                  type="submit"
                  disabled={loading || lineItems.length === 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {isEditMode ? 'Update Invoice' : 'Create Invoice'}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/invoices')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Custom Product Modal */}
      {showCustomProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Add Custom Product</h3>
              <button
                type="button"
                onClick={() => setShowCustomProductModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter product name"
                  id="custom-product-name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    placeholder="1"
                    min="0.01"
                    step="0.01"
                    defaultValue="1"
                    id="custom-product-quantity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Price *
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    id="custom-product-price"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCustomProductModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const name = (document.getElementById('custom-product-name') as HTMLInputElement)?.value
                    const quantity = parseFloat((document.getElementById('custom-product-quantity') as HTMLInputElement)?.value || '1')
                    const price = parseFloat((document.getElementById('custom-product-price') as HTMLInputElement)?.value || '0')

                    if (!name || !price) {
                      setError('Product name and price are required')
                      return
                    }

                    // Add custom product as line item
                    setLineItems([
                      ...lineItems,
                      {
                        product_id: `custom-${Date.now()}`,
                        product_name: name,
                        quantity: quantity,
                        unit_price: price,
                        total_price: quantity * price
                      }
                    ])

                    setShowCustomProductModal(false)
                    setError(null)
                  }}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Add Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default InvoiceForm
